import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useBuyerAuth } from '../../contexts/BuyerAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import QueryHistoryTab from './components/QueryHistoryTab';
import OrderStatusTab from './components/OrderStatusTab';
import FavoritesTab from './components/FavoritesTab';
import CommunicationsTab from './components/CommunicationsTab';
import ProfileTab from './components/ProfileTab';
import NotificationsTab from './components/NotificationsTab';
import AIRecommendationsTab from './components/AIRecommendationsTab';
import { MessagesTab } from './components/MessagesTab';

const content = {
  en: {
    favorites: 'Favorites',
    orders: 'Orders',
    queries: 'Queries',
    communications: 'Communications',
    analytics: 'Analytics',
    profile: 'Profile',
    aiRecommendations: 'AI Recommendations',
    messages: 'Messages'
  },
  zh: {
    favorites: '收藏',
    orders: '订单',
    queries: '查询',
    communications: '通讯',
    analytics: '分析',
    profile: '个人资料',
    aiRecommendations: 'AI推荐',
    messages: '消息'
  }
};

const BuyerDashboard = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('orders');
  const { user, userProfile, loading, isAuthenticated } = useBuyerAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/buyer-login');
    }
  }, [loading, isAuthenticated, navigate]);

  const tabs = [
    { id: 'favorites', label: content?.[language]?.favorites, icon: 'Heart' },
    { id: 'orders', label: content?.[language]?.orders, icon: 'ShoppingCart' },
    { id: 'queries', label: content?.[language]?.queries, icon: 'MessageSquare' },
    { id: 'messages', label: content?.[language]?.messages, icon: 'MessageCircle' },
    { id: 'communications', label: content?.[language]?.communications, icon: 'Mail' },
    { id: 'notifications', label: language === 'en' ? 'Notifications' : '通知', icon: 'Bell' },
    { id: 'ai-recommendations', label: content?.[language]?.aiRecommendations, icon: 'Sparkles' },
    { id: 'analytics', label: content?.[language]?.analytics, icon: 'BarChart3' },
    { id: 'profile', label: content?.[language]?.profile, icon: 'User' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'orders':
        return <OrderStatusTab language={language} />;
      case 'queries':
        return <QueryHistoryTab language={language} />;
      case 'favorites':
        return <FavoritesTab language={language} />;
      case 'messages':
        return <MessagesTab />;
      case 'communications':
        return <CommunicationsTab language={language} />;
      case 'ai-recommendations':
        return <AIRecommendationsTab language={language} />;
      case 'analytics': navigate('/buyer-analytics-dashboard');
        return null;
      case 'profile':
        return <ProfileTab language={language} />;
      default:
        return <OrderStatusTab language={language} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {language === 'en' ? 'Loading...' : '加载中...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <FullWidthSection padding="px-5" py="py-8">
          <ContentWrapper maxWidth="6xl">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="User" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  {language === 'en' ? 'Buyer Dashboard' : '买家仪表板'}
                </h1>
                <p className="text-muted-foreground">
                  {language === 'en' ? `Welcome back, ${userProfile?.full_name || 'Buyer'}` : `欢迎回来，${userProfile?.full_name || '买家'}`}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-smooth whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {renderTabContent()}

              {activeTab === 'notifications' && <NotificationsTab language={language} />}
            </div>
          </div>
          </ContentWrapper>
        </FullWidthSection>
      </main>
    </div>
  );
};

export default BuyerDashboard;